import React, { useEffect, useState, useMemo, useRef } from "react";
import moment from "moment";
import "./style.scss";
import AttachmentIcon from "../../assets/icons/components/AttachmentIcon";
import { _global } from "../../helpers";
import { RightArrowIcon, DownArrowIcon, ArrowUpIcon, ArrowDownIcon, ArrowUpDownIcon } from "../../assets/icons/components";
import { useDispatch, useSelector } from "react-redux";
import SkeletonText from "../skeletons/SkeletonText";
import actions from "./../../actions/action.conversations";
import { downloadAttachment } from "./../../actions/action.messageList";
import { unstable_batchedUpdates } from "react-dom";
import { Alert } from "reactstrap";
import { Button } from "@centivo/react-components";
import ReactMarkdown from "react-markdown";
import rehypeRaw from 'rehype-raw';
import { changeToTitleCase, formatInAppMessageHtml, getConversationMessageCount } from "../../utils/convertionUtils";
import { exportInboxToExcel } from "./inboxExportExcelJs";
import { currentAgeInDays } from "./inboxExport";

const getContent = (path, obj, separator = ".") => {
  const properties = Array.isArray(path) ? path : path.split(separator);
  return properties.reduce((prev, curr) => prev && prev[curr], obj);
};

const CURRENT_AGE_SORT = "currentAgeDays";

const getSortValue = (path, obj) => {
  if (path === CURRENT_AGE_SORT) {
    const age = currentAgeInDays(obj.currentMemberRequestInitialMessageDate);
    return age === "" ? null : age;
  }
  const raw = getContent(path, obj);
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number") return raw;
  const asDate = moment(raw, moment.ISO_8601, true);
  return asDate.isValid() ? asDate.valueOf() : String(raw).toLowerCase();
};

const compareValues = (aValue, bValue, ascending) => {
  if (aValue === null && bValue === null) return 0;
  if (aValue === null) return 1;
  if (bValue === null) return -1;
  if (aValue === bValue) return 0;
  if (ascending) return aValue < bValue ? -1 : 1;
  return aValue > bValue ? -1 : 1;
};

const DEFAULT_SORT_ATTRIBUTE = "lastMessage.createdTimestamp";
const SECONDARY_SORT_PATHS = { currentMemberRequestInitialMessageDate: DEFAULT_SORT_ATTRIBUTE };

// Missing primary values stay at the bottom, ordered among themselves by secondaryPath.
const compareByAttribute = (path, ascending, secondaryPath) => (a, b) => {
  const aValue = getSortValue(path, a);
  const bValue = getSortValue(path, b);
  const primary = compareValues(aValue, bValue, ascending);
  if (primary !== 0 || !secondaryPath) return primary;
  return compareValues(getSortValue(secondaryPath, a), getSortValue(secondaryPath, b), ascending);
};

const ConversationTable = ({ conversations = { Items: [] }, member, openReplyModal, filter, setFilter }) => {
  const [search, setSearch] = useState("");

  const [order, setOrder] = useState(true);
  const [sortingBy, setSortingBy] = useState(DEFAULT_SORT_ATTRIBUTE);

  const [savedConversations, /*setSavedConversation*/] = useState([]);
  const [sortedConversation, setSortedConversation] = useState([]);

  const [showMessages, setShowMessages] = useState(false);

  //pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [conversationPerPage, setConversationPerPage] = useState(10);

  const indexOfLastConversation = currentPage * conversationPerPage;
  const indexOfFirstConversation = indexOfLastConversation - conversationPerPage;
  const currentConversations = sortedConversation.slice(indexOfFirstConversation, indexOfLastConversation);

  const showCurrentAge = filter === "inbox" && !member?.memberUuid && !member?.sequencedMemberId;
  const columnCount = showCurrentAge ? 12 : 11;

  const dispatch = useDispatch();
  const { messages, loading, error, loadingMessages } = useSelector((state) => state.conversations);

  const filterSortedConversation = useMemo(() => {
    return () => {
      if (search) {
        setSortedConversation(sortedConversation.filter((con) => con.conversationId.toLowerCase().includes(search.toLowerCase())));
      } else {
        setSortedConversation(savedConversations);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    //  console.log(search)
    filterSortedConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    setSearch("");
    setOrder(false);
    setSortingBy(DEFAULT_SORT_ATTRIBUTE);
    let sortedList = [...conversations.Items]
      .filter(c => c.lastMessage)
      .sort(compareByAttribute(DEFAULT_SORT_ATTRIBUTE, false));
    setSortedConversation(sortedList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations.fetchedAt]);

  const sortTable = (sorting) => {
    unstable_batchedUpdates(() => {
      setOrder(sorting.order);
      setSortingBy(sorting.attribute);
      setSortedConversation(
        [...sortedConversation].sort(compareByAttribute(
          sorting.attribute,
          sorting.order,
          SECONDARY_SORT_PATHS[sorting.attribute],
        ))
      );
    });
  };

  const setFilterHandler = (tabName) => {
    unstable_batchedUpdates(() => {
      setShowMessages(false);
      setFilter(tabName);
      setCurrentPage(1);
    });
  };

  const showMessagesHandler = (conversation) => {
    if (conversation.conversationId === showMessages) {
      //hdie the messages
      setShowMessages(false);
    } else {
      setShowMessages(conversation.conversationId);
      dispatch(actions.getMessagesByConversationIdAction(conversation));
    }
  };

  const changeStatusHandler = (conversation, status) => {
    dispatch(actions.changeConversationStatusAction({ ...conversation, status }, filter));
  };

  const showAttachment = (message, conversation) => {
    return (
      <div className="email-attachments-wrapper">
        {message.attachments &&
          message.attachments.map((attachment) => {
            return (
              <div key={attachment.Key} className="email-attachment">
                <div className="top">
                  <div className="card-icon">{_global.getIconBasedOnFileType(attachment.type)}</div>
                  <div className="file-desc">
                    <p>{attachment.name}</p>
                    <p style={{ fontSize: "x-small" }}>{_global.niceBytes(attachment.size)}</p>
                  </div>
                </div>
                <div className="buttom">
                  <p onClick={() => dispatch(downloadAttachment({ Key: attachment.Key, filename: attachment.name, conversationId: conversation.conversationId, recipientId: conversation.recipientId }))}>Download</p>
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const showMessagesPlaceHodler = (count) => {
    return [...Array(count)].map((e, i) => (
      <div className="message-card" key={i}>
        <div className="message-header">
          <SkeletonText style={{ width: "20%", height: "20px" }} />
          <SkeletonText style={{ width: "20%", height: "20px" }} />
        </div>
        {/* <SkeletonText style={{ width: '30%', height: '30px' }} /> */}
        <SkeletonText style={{ width: "100%", height: "20px" }} />
      </div>
    ));
  };

  const pagination = (number) => {
    setCurrentPage(number);
  };

  // A ref rather than state so repeat clicks are ignored without re-rendering the button into its disabled style.
  const exportingRef = useRef(false);
  const exportHandler = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    try {
      await exportInboxToExcel(sortedConversation, member);
    } catch (e) {
      console.error("Inbox export failed", e);
    } finally {
      exportingRef.current = false;
    }
  };

  return (
    <div className="conversationTable">
      <Alert color="danger" isOpen={error ? true : false} style={{ marginTop: "20px" }}>
        {error}
      </Alert>
      <div className="tab-options">
        <div className={`tab-action ${filter === "inbox" ? " selected" : null}`} onClick={() => setFilterHandler("inbox")}>
          Inbox
        </div>
        <div className={`tab-action ${filter === "complete" ? " selected" : null}`} onClick={() => setFilterHandler("complete")}>
          Complete
        </div>
      </div>

      <div className='content-wrapper'>
        {filter === "inbox" && (
          <div className="export-btn-wrapper">
            <Button
              type="button"
              className="inboxExportButton"
              isDisabled={!sortedConversation.length}
              onClick={exportHandler}
            >
              Export
            </Button>
          </div>
        )}
        <Pagination
          totalConversations={sortedConversation.length}
          currentConversations={currentConversations}
          conversationPerPage={conversationPerPage}
          indexOfFirstConversation={indexOfFirstConversation}
          pagination={pagination}
          setConversationPerPage={setConversationPerPage}
          currentPage={currentPage}
        />

        <table>
          <thead>
            <tr>
              <th></th>
              <th
                onClick={() => {
                  sortTable({ order: !order, attribute: "lastMessage.createdTimestamp" });
                }}
              >
                Last Message Date
                {sortingBy === "lastMessage.createdTimestamp" ? order ? <ArrowUpIcon /> : <ArrowDownIcon /> : <ArrowUpDownIcon />}
              </th>
              <th
                onClick={() => {
                  sortTable({ order: !order, attribute: "currentMemberRequestInitialMessageDate" });
                }}
              >
                Initial Message Date
                {sortingBy === "currentMemberRequestInitialMessageDate" ? order ? <ArrowUpIcon /> : <ArrowDownIcon /> : <ArrowUpDownIcon />}
              </th>
              <th></th>
              <th>MEMBER NAME</th>
              <th>CLIENT NAME</th>
              <th>MEMBER ID</th>
              <th>VENDOR</th>
              <th>MEMBER EMAIL</th>
              <th>PHONE</th>
              <th style={{ width: '400px' }}>subject</th>
              {showCurrentAge && (
                <th
                  onClick={() => {
                    sortTable({ order: !order, attribute: CURRENT_AGE_SORT });
                  }}
                >
                  Current Age (days)
                  {sortingBy === CURRENT_AGE_SORT ? order ? <ArrowUpIcon /> : <ArrowDownIcon /> : <ArrowUpDownIcon />}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentConversations.map((conversation, index) => {
              const currentAge = showCurrentAge
                ? currentAgeInDays(conversation.currentMemberRequestInitialMessageDate)
                : "";
              return (
                <React.Fragment key={conversation.conversationId}>
                  <tr onClick={() => showMessagesHandler(conversation)} className={showMessages === conversation.conversationId ? 'selected-row' : ''}>
                    <td className="icon-arrow">
                      {getConversationMessageCount(conversation) > 1 ? showMessages === conversation.conversationId ? <DownArrowIcon /> : <RightArrowIcon /> : null}
                    </td>
                    <td>
                      {moment(conversation.lastMessage.createdTimestamp).format("MM/DD/YYYY")}
                    </td>
                    <td>
                      {conversation.currentMemberRequestInitialMessageDate
                        ? moment(conversation.currentMemberRequestInitialMessageDate).format("MM/DD/YYYY")
                        : "-"}
                    </td>
                    <td>
                      {
                        (!isNaN(conversation.lastMessage.attachments) && conversation.lastMessage.attachments > 0) ||
                        (Array.isArray(conversation.lastMessage.attachments) && conversation.lastMessage.attachments.length) 
                          ? <AttachmentIcon /> 
                          : ""
                      }
                    </td>
                    <td>
                      {
                        conversation.member 
                          ? changeToTitleCase(conversation.member.firstName + " " + conversation.member.lastName) 
                          : member 
                            ? changeToTitleCase(member.name) 
                            : null
                      }
                    </td>
                    <td>
                      {(member?.clientName || conversation?.member?.clientName) ?? '-'}
                    </td>
                    <td>
                      {member.sequencedMemberId || (conversation.member && conversation.member.sequencedMemberId)}
                    </td>
                    <td>
                      {conversation?.member?.memberHccId || member?.memberHccId ? 'HealthRules Payer' : "Javelina"}
                    </td>
                    <td>
                      {member.email || (conversation.member && conversation.member.emailAddress)}
                    </td>
                    <td>
                      {_global.formatPhoneNumber(member.phone || (conversation.member && conversation.member.phoneNumber))}
                    </td>
                    <td>{conversation.topic}</td>
                    {showCurrentAge && <td>{currentAge === "" ? "-" : currentAge}</td>}
                  </tr>

                  {showMessages === conversation.conversationId ? (
                    <tr className="expanded">
                      <td colSpan={columnCount}>
                        <div className="message-cards">
                          {messages && messages.Items.length
                            ? messages.Items.map((message, index) => (
                              <div className="message-card" key={message.messageId + index}>
                                <div className="message-header">
                                  <div className="senderFullName"><p>{message.senderFullName}</p></div>

                                  <div className="createdTimestamp">{moment(message.createdTimestamp).format("MMMM Do, YYYY hh:mm a")}</div>
                                </div>
                                {message.attachments ? showAttachment(message, conversation) : null}
                                <div className="message-body">
                                  <ReactMarkdown rehypePlugins={[rehypeRaw]} children={formatInAppMessageHtml(message.message)} />
                                </div>
                              </div>
                            ))
                            : showMessagesPlaceHodler(getConversationMessageCount(conversation) - 1)}
                          {loadingMessages && showMessagesPlaceHodler(1)}

                          {messages && messages.Items.length !== getConversationMessageCount(conversation) && messages.LastEvaluatedKey && (
                            <div className="load-more-btn" onClick={() => dispatch(actions.getMessagesByConversationIdAction(conversation, messages.LastEvaluatedKey))}>
                              Load more...
                            </div>
                          )}
                          <div className="action-row">
                            <div className="reply-btn-wrapper">
                              <button
                                className="btn btn-transparent btn-reply"
                                onClick={() => {
                                  openReplyModal(conversation);
                                }}
                              >
                                <span className="icon icon-corner-up-left" />
                                Reply
                              </button>
                            </div>
                            {filter === "inbox" && (
                              <div
                                className="move-message-container"
                                onClick={() => {
                                  changeStatusHandler(conversation, "COMPLETE");
                                }}
                              >
                                <input type="checkbox" className="move-message-checkbox" />
                                <i className={`icon icon-circle icon-circle-unchecked`} />
                                <label className="move-message-label">Move to complete</label>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {conversations.LastEvaluatedKey && (
          <div
            className="load-more-btn"
            onClick={() => {
              setShowMessages(false);
              if (member.memberUuid) {
                dispatch(actions.getConversationsByRecipientIdAction(member.memberUuid, conversations.LastEvaluatedKey, filter));
              } else {
                if (filter === "inbox") {
                  dispatch(actions.getConversationsByStatusAction("CSR_ACTION_REQUIRED", conversations.LastEvaluatedKey, filter));
                } else if (filter === "complete") {
                  dispatch(actions.getConversationsByStatusAction("COMPLETE", conversations.LastEvaluatedKey, filter));
                }
              }
            }}
          >
            {loading ? "Loading" : " Load more..."}
          </div>

        )}
      </div>
    </div>
  );
};

export default ConversationTable;

const Pagination = ({ conversationPerPage, currentConversations, totalConversations, indexOfFirstConversation, pagination, setConversationPerPage, currentPage }) => {
  const pageNumbers = [];

  for (let i = 1; i <= Math.ceil(totalConversations / conversationPerPage); i++) {
    pageNumbers.push(i);
  }
  return (
    <nav className='pagination-navbar'>
      <span>      Records: {indexOfFirstConversation + 1} - {+indexOfFirstConversation + +currentConversations.length} / {totalConversations}</span>

      <select
        id="dropdown"
        onChange={(e) => {
          pagination(1);
          setConversationPerPage(e.target.value);
        }}
      >
        <option value="10">10</option>
        <option value="25">25</option>
        <option value="50">50</option>
        <option value="100">100</option>
      </select>
      <span>per page</span>
      <span>    {currentPage} / {pageNumbers.length}</span>

      <div className='pagination-arrow-botton-wrapper'>
        <div onClick={() => currentPage <= 1 ? null : pagination(currentPage - 1)}>
          <i className="icon icon-chevron-left" />
        </div>
        <div onClick={() => currentPage < pageNumbers.length ? pagination(currentPage + 1) : null}>
          <i className="icon icon-chevron-right" />
        </div>
      </div>

    </nav>
  );
};
